/**
 * Validation Utilities
 * Matches backend validation rules
 */

// Validation rules
export const rules = {
  // Mobile: Indian 10-digit starting with 6-9
  mobile: {
    pattern: /^[6-9]\d{9}$/,
    message: 'Enter valid 10-digit mobile number',
  },
  
  // Email
  email: {
    pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
    message: 'Enter valid email address',
  },
  
  // Full name: 2-100 characters
  fullName: {
    minLength: 2,
    maxLength: 100,
    message: 'Name must be 2-100 characters',
  },
  
  // OTP: 6 digits
  otp: {
    pattern: /^\d{6}$/,
    message: 'OTP must be 6 digits',
  },
  
  // Company name
  companyName: {
    maxLength: 200,
    message: 'Company name too long',
  },
  
  // Designation
  designation: {
    maxLength: 100,
    message: 'Designation too long',
  },
  
  // Message
  message: {
    minLength: 10,
    maxLength: 1000,
    message: 'Message must be 10-1000 characters',
  },
  
  // Subject
  subject: {
    minLength: 3,
    maxLength: 200,
    message: 'Subject must be 3-200 characters',
  },
};

/**
 * Validate a single field
 * @returns {string|null} Error message or null if valid
 */
export const validateField = (name, value) => {
  const trimmedValue = typeof value === 'string' ? value.trim() : value;
  
  switch (name) {
    case 'mobile': {
      if (!trimmedValue) return 'Mobile number is required';
      const cleanMobileVal = trimmedValue.replace(/\D/g, '').slice(-10);
      if (!rules.mobile.pattern.test(cleanMobileVal)) {
        return rules.mobile.message;
      }
      return null;
    }
    
    case 'email': {
      if (!trimmedValue) return 'Email is required';
      if (!rules.email.pattern.test(trimmedValue)) {
        return rules.email.message;
      }
      return null;
    }
    
    case 'fullName':
    case 'name': {
      if (!trimmedValue) return 'Name is required';
      if (trimmedValue.length < rules.fullName.minLength) {
        return 'Name is too short';
      }
      if (trimmedValue.length > rules.fullName.maxLength) {
        return 'Name is too long';
      }
      return null;
    }
    
    case 'otp': {
      if (!trimmedValue) return 'OTP is required';
      // Allow "00000" or "000000" for development mode or standard 6-digit OTP
      if (trimmedValue !== '00000' && trimmedValue !== '000000' && !rules.otp.pattern.test(trimmedValue)) {
        return rules.otp.message;
      }
      return null;
    }
    
    case 'companyName': {
      if (trimmedValue && trimmedValue.length > rules.companyName.maxLength) {
        return rules.companyName.message;
      }
      return null;
    }
    
    case 'designation': {
      if (trimmedValue && trimmedValue.length > rules.designation.maxLength) {
        return rules.designation.message;
      }
      return null;
    }
    
    case 'gender': {
      if (trimmedValue && !['male', 'female', 'other'].includes(trimmedValue)) {
        return 'Select a valid gender';
      }
      return null;
    }
    
    case 'message': {
      if (!trimmedValue) return 'Message is required';
      if (trimmedValue.length < rules.message.minLength) {
        return 'Message is too short';
      }
      if (trimmedValue.length > rules.message.maxLength) {
        return 'Message is too long';
      }
      return null;
    }
    
    case 'subject': {
      if (!trimmedValue) return 'Subject is required';
      if (trimmedValue.length < rules.subject.minLength) {
        return 'Subject is too short';
      }
      if (trimmedValue.length > rules.subject.maxLength) {
        return 'Subject is too long';
      }
      return null;
    }
    
    default:
      return null;
  }
};

/**
 * Validate multiple fields
 * @returns {Object} { isValid, errors }
 */
export const validateForm = (fields, requiredFields = []) => {
  const errors = {};
  
  // Check required fields
  for (const field of requiredFields) {
    if (!fields[field] || (typeof fields[field] === 'string' && !fields[field].trim())) {
      errors[field] = `${formatFieldName(field)} is required`;
    }
  }
  
  // Validate all fields
  for (const [name, value] of Object.entries(fields)) {
    if (!errors[name]) {
      const error = validateField(name, value);
      if (error) {
        errors[name] = error;
      }
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Format field name for display
 */
const formatFieldName = (name) => {
  const names = {
    fullName: 'Full name',
    name: 'Name',
    email: 'Email',
    mobile: 'Mobile number',
    otp: 'OTP',
    companyName: 'Company name',
    designation: 'Designation',
    gender: 'Gender',
    message: 'Message',
    subject: 'Subject',
  };
  return names[name] || name;
};

/**
 * Format mobile for display
 */
export const formatMobile = (mobile) => {
  if (!mobile) return '';
  const clean = mobile.replace(/\D/g, '').slice(-10);
  return `+91 ${clean.slice(0, 5)} ${clean.slice(5)}`;
};

/**
 * Clean mobile number (extract 10 digits)
 */
export const cleanMobile = (mobile) => {
  if (!mobile) return '';
  return mobile.replace(/\D/g, '').slice(-10);
};

// ==================== Legacy/Compatibility Exports ====================

/**
 * Validate full name (legacy export)
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateFullName = (name) => {
  const error = validateField('fullName', name);
  return {
    isValid: !error,
    error,
  };
};

/**
 * Validate email with detailed response (legacy export)
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateEmailDetailed = (email) => {
  const error = validateField('email', email);
  return {
    isValid: !error,
    error,
  };
};

/**
 * Validate contact form (legacy export)
 * @returns {{ isValid: boolean, errors: Object }}
 */
export const validateContactForm = (formData) => {
  const requiredFields = ['name', 'email', 'message'];
  return validateForm(formData, requiredFields);
};

/**
 * Validate mobile (legacy export)
 * @returns {{ isValid: boolean, error: string|null }}
 */
export const validateMobile = (mobile) => {
  const error = validateField('mobile', mobile);
  return {
    isValid: !error,
    error,
  };
};

/**
 * Validate login form (legacy export)
 * @returns {{ isValid: boolean, errors: Array }}
 */
export const validateLoginForm = (formData) => {
  const errors = [];
  
  // Mobile validation
  if (!formData.mobileNumber) {
    errors.push({ field: 'mobileNumber', message: 'Mobile number is required' });
  } else if (!/^\d{10}$/.test(formData.mobileNumber)) {
    errors.push({ field: 'mobileNumber', message: 'Please enter a valid 10-digit mobile number' });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Validate registration form (legacy export)
 * @returns {{ isValid: boolean, errors: Array }}
 */
export const validateRegistrationForm = (formData) => {
  const errors = [];
  
  // Full name validation
  if (!formData.fullName || formData.fullName.trim().length < 2) {
    errors.push({ field: 'fullName', message: 'Full name is required (min 2 characters)' });
  }
  
  // Email validation
  if (!formData.email) {
    errors.push({ field: 'email', message: 'Email is required' });
  } else if (!rules.email.pattern.test(formData.email)) {
    errors.push({ field: 'email', message: 'Please enter a valid email address' });
  }
  
  // Mobile validation
  if (!formData.mobile) {
    errors.push({ field: 'mobile', message: 'Mobile number is required' });
  } else if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
    errors.push({ field: 'mobile', message: 'Please enter a valid 10-digit mobile number' });
  }
  
  // Date of birth validation - simple check
  if (!formData.day || !formData.month || !formData.year) {
    errors.push({ field: 'dateOfBirth', message: 'Date of birth is required' });
  } else {
    // Optional: Validate age (must be at least 18 years old)
    const birthDate = new Date(`${formData.year}-${formData.month}-${formData.day}`);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    const dayDiff = today.getDate() - birthDate.getDate();
    const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
    
    if (actualAge < 18) {
      errors.push({ field: 'dateOfBirth', message: 'You must be at least 18 years old' });
    }
  }
  
  // Gender validation
  if (!formData.gender) {
    errors.push({ field: 'gender', message: 'Gender is required' });
  }
  
  // Place validation
  if (!formData.place) {
    errors.push({ field: 'place', message: 'Place is required' });
  }
  
  // Profession validation
  if (!formData.profession) {
    errors.push({ field: 'profession', message: 'Profession is required' });
  }
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};
